import React, { useCallback, useEffect, useMemo, useState } from 'react';


import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Box, Button, ContentTemplate, Grid, Progress, Typography } from 'web-library';
import { ResitSubject } from '../../../../api/candidate/resit/model';
import { UpdateResitCandidateSelectedSubjects } from '../../../../store/resitSlice';
import { useResitNavigation } from '../../../../routes/ResitRoute';
import SubjectSelectionTable from '../SubjectSelectionTable';
import { useWOGAA } from '../../../../hooks/wogaa';

import { useTriggerMutate } from '../../../../hooks/query';
import { usePostCheckEligibility } from '../../../../api-refactored/resit-registration/usePostCheckEligibility';
import { useGetResitSubjects } from '../../../../api-refactored/resit-registration/useGetResitSubjects';

const useStyles = makeStyles()((theme) =>
({
  container: {
    alignItems: 'center',
  },
  button: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
  },
  errorText: {
    color: theme.color.state.stateError,
  },
}));

export interface SelectSubjectProps {
  selectedSubjects: ResitSubject[];
  routeSteps: string[];
}

const SelectSubject = ({ next }: { next: (subjects: number[]) => void }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { toInstruction, accessToken } = useResitNavigation();
  const [errMsg, setErrMsg] = useState('');
  const [selectionArr, setSelectionArr] = useState<number[]>([]);
  const [disabledButton, setDisabledButton] = useState<boolean>(true);

  const { transactionStart } =  useWOGAA('myexamsseab-3753');

  /** WOGAA transaction tracking start for resit by candidate
   * */
  transactionStart();

  const { mutate: postCheckEligibility } = usePostCheckEligibility({
    onSuccess: (response) => {
      if (response.error?.code === '5001') {
        setDisabledButton(true);
      }
    },
    onError: () => {
      setDisabledButton(true);
    },
  });

  const callMutation = useCallback(() => {
    dispatch(UpdateResitCandidateSelectedSubjects([]));
    if (!accessToken) {
      // normal
      postCheckEligibility({ accessLink: !!accessToken });
    }
  }, [dispatch, postCheckEligibility, accessToken]);
  const setTriggerMutate = useTriggerMutate(callMutation);

  useEffect(() => {
    setTriggerMutate((prev) => prev + 1);
  }, [setTriggerMutate]);

  /** get resit subject
   * */ const { data: resitSubject, isLoading, error } = useGetResitSubjects({
    token: accessToken,
  });

  /** define grid row data
   * */
  const rows = useMemo((): ResitSubject[] => {
    if (!resitSubject) {
      return [];
    }
    return resitSubject.map(
      (item: any, index): ResitSubject => ({
        registered: item.registered,
        subjectCode: item.subjectCode,
        subject: item.subject,
        examSeries: item.examSeries,
        examLevel: item.examLevel,
      })
    );
  }, [resitSubject]);

  if (isLoading) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  if (error || !resitSubject) {
    return <Typography>{t('resit->>RegisterResit.Error getting access config data.')}</Typography>;
  }

  /** to handle subject selection
   * */
  const onSelectSubject = (selectedIndex: number[]) => {
    setErrMsg('');
    setSelectionArr(selectedIndex);
  };

  /** to handle clear subject selection
   * */
  const onClearSubjectSelection = () => {
    setSelectionArr([]);
    setDisabledButton(true);
  };

  /** store selected subjects information into redux store(resit) upon candidate confirmation
   * */
  const onConfirm = () => {
    if (
      Array.isArray(selectionArr) &&
      Array.isArray(resitSubject) &&
      selectionArr.length > 0 &&
      resitSubject.length > 0
    ) {
      const selectedSubjects = selectionArr.map((selectedIndex) => resitSubject[selectedIndex]);
      dispatch(UpdateResitCandidateSelectedSubjects(selectedSubjects));
      next(selectionArr);
    } else {
      setErrMsg('Please select the subjects.');
    }
  };

  /** to handle go back previous page/action
   * */
  const onBack = () => {
    dispatch(UpdateResitCandidateSelectedSubjects([]));
    history.push(toInstruction);
  };

  return (
    (<Box>
      <ContentTemplate isContinuable type="full">
        <SubjectSelectionTable
          rows={rows}
          onSelectionChange={onSelectSubject}
          onClearSelection={onClearSubjectSelection}
        />
      </ContentTemplate>
      <ContentTemplate continuous>
        <Grid container direction="row" alignItems="flex-start" justify="flex-start">
          <Grid xs={12}>
            {errMsg && (
              <Typography className={classes.errorText} variant="body2">
                {errMsg}
              </Typography>
            )}
          </Grid>
        </Grid>

        <Grid container direction="row" alignItems="flex-start" spacing={3}>
          <Grid xs={12} md={3}>
            <Button
              id="button-selectSubject-back"
              fullWidth
              className={classes.button}
              variant="outlined"
              onClick={() => {
                onBack();
              }}
            >
              {t('resit:RegisterResit.Back')}
            </Button>
          </Grid>

          <Grid xs={12} md={4}>
            <Button
              id="button-selectSubject-continueToNextPage"
              fullWidth
              className={classes.button}
              onClick={onConfirm}
              disabled={disabledButton && selectionArr.length === 0}
            >
              {t('resit:RegisterResit.Continue to next page')}
            </Button>
          </Grid>
        </Grid>
      </ContentTemplate>
    </Box>)
  );
};

export default SelectSubject;
